import React, { useState } from 'react';
import _get from 'lodash/get';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { getImageFile } from 'utils/stringUtils';
import CloseIcon from '@material-ui/icons/Close';
import { MenuIcon } from './components/icons/MenuIcon';
import { ModuleQueue } from './components/ModuleQueue';
import { CourseSidebarInformation } from './components/CourseSidebarInformation';
import { getActiveTab } from './utils';
import { COURSE_SIDEBAR_TABS } from './constants';

const useStyles = makeStyles(theme => ({
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    padding: '20px',
    gap: 10,
    width: 398,
    [theme.breakpoints.between('961', '1200')]: {
      width: 300,
    },
    boxShadow: '0 2px 24px 10px rgba(0,0,0,0.05)',
  },
  dialogPaper: {
    padding: theme.spacing(2),
    borderRadius: '8px !important',
  },
  iconButton: {
    marginLeft: '-12px',
    marginRight: theme.spacing(1),
  },
  closeButton: {
    padding: 6,
  },
  imageContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    height: 110,
    '& img': {
      maxHeight: '100%',
      zIndex: 1,
    },

    '&:before': {
      content: "''",
      position: 'absolute',
      filter: 'blur(10px)',
      inset: '0px',
      background: `center center  / cover no-repeat`,
      backgroundImage: props => `url(${props.image})`,
    },
  },
  title: {
    fontSize: '1.125rem',
  },
}));

export const CourseSidebar = ({
  item,
  isMobile,
  isTablet,
  resourceItemPageSiteCopy,
  coursesSiteCopy,
  activeModule,
  activeResourceIdx,
  activeSeries,
  onResourceClick,
  seriesProgress,
  completedSeriesIds,
  onSeriesClick,
  onRecommendedTabClick,
  trackingPath,
}) => {
  const image = getImageFile(item.fields);
  const classes = useStyles({ image, isMobile });
  const inOverlay = isMobile || isTablet;
  const [isOpen, setIsOpen] = useState(false);

  const onSidebarRefSet = node => {
    if (node) {
      const header = document.getElementById('app-header');
      // eslint-disable-next-line no-param-reassign
      node.style.minHeight = `${window.innerHeight - header.offsetHeight}px`;
    }
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const handleResourceClick = (...args) => {
    if (inOverlay) {
      setIsOpen(false);
    }

    onResourceClick(...args);
  };

  const handleRecommendedTabClick = (...args) => {
    if (inOverlay) {
      setIsOpen(false);
    }

    onRecommendedTabClick(...args);
  };

  const handleSeriesClick = (series, seriesIdx) => {
    const activeTab = getActiveTab(series, null);
    if (activeTab !== COURSE_SIDEBAR_TABS.RESOURCE) {
      setIsOpen(false);
      onSeriesClick(series, seriesIdx);
    }
  };

  const activeModuleTitle = _get(activeModule, 'fields.title');
  const content = (
    <section className={classes.container}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className={classes.title} color="textPrimary" variant="h6">
          {activeModuleTitle}
        </Typography>
        {inOverlay && (
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close Menu"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <ModuleQueue
        course={item}
        activeSeries={activeSeries}
        activeModule={activeModule}
        activeResourceIdx={activeResourceIdx}
        resourceItemPageSiteCopy={resourceItemPageSiteCopy}
        coursesSiteCopy={coursesSiteCopy}
        onResourceClick={handleResourceClick}
        seriesProgress={seriesProgress}
        completedSeriesIds={completedSeriesIds}
        onSeriesClick={handleSeriesClick}
        onRecommendedTabClick={handleRecommendedTabClick}
        trackingPath={trackingPath}
      />
    </section>
  );

  return inOverlay ? (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        className={classes.iconButton}
        aria-label="Open Menu"
      >
        <MenuIcon />
      </IconButton>
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        open={isOpen}
        onClose={onClose}
      >
        {content}
      </Dialog>
    </>
  ) : (
    <aside className={classes.leftPanel} ref={onSidebarRefSet}>
      <CourseSidebarInformation
        coursesSiteCopy={coursesSiteCopy}
        item={item}
        inSidebar
      />
      {content}
    </aside>
  );
};
